import React, { useEffect, useState } from "react"
import { Button, SubmitButton } from "../shared/Button"
import { useMutation } from "react-query"
import { mutationRequest } from "../../util/request"
import type { AuthFlowData } from "./AuthenticationDialog"

interface ResetPasswordFormProps {
  setModalState: (state: "auth" | "register" | "reset_password") => void
  validationError: (data: AuthFlowData) => string | null
  closeDialog: () => void
}

interface ResetPasswordUser {
  email: string | null
}

interface ResetPasswordData extends AuthFlowData {
  flow: "reset_password"
  current_location: string
  user: ResetPasswordUser
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({
  setModalState,
  validationError,
  closeDialog,
}) => {
  const [resetPasswordErrorMessage, setResetPasswordErrorMessage] = useState("")
  const [resetPasswordValidMessage, setResetPasswordValidMessage] = useState("")
  const mutation = useMutation(mutationRequest)
  const isSubmitting = mutation.isLoading
  const [resetPasswordData, setResetPasswordData] = useState<ResetPasswordData>(
    {
      flow: "reset_password",
      current_location: window.location.href,
      user: {
        email: null,
      },
    },
  )

  const setResetPasswordField = (
    field: keyof ResetPasswordUser,
    value: string | null,
  ) => {
    setResetPasswordData((prev) => ({
      ...prev,
      user: {
        ...prev.user,
        [field]: value,
      },
    }))
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setResetPasswordErrorMessage("")
    const errorMessage = validationError(resetPasswordData)
    if (errorMessage) {
      setResetPasswordErrorMessage(errorMessage)
      return
    }
    try {
      mutation.mutate({
        options: { method: "POST" },
        path: `/api/v1/users`,
        data: resetPasswordData,
      })
    } catch {
      setResetPasswordErrorMessage("Sorry, there was an unknown error")
    }
  }

  useEffect(() => {
    if (mutation.status === "success") {
      const data = mutation.data as {
        success?: boolean
        errors?: { error: string }
      }
      if (data.success) {
        setResetPasswordValidMessage(
          "Check your email (spam) for reset password instructions from bandnada.com",
        )
      } else {
        setResetPasswordErrorMessage(data.errors?.error ?? "Unknown error")
      }
    } else if (mutation.status === "error") {
      setResetPasswordErrorMessage("Sorry, there was an unknown error")
    }
  }, [mutation.status, mutation.data])

  return (
    <>
      <div className="lg:col-span-9 col-span-12 lg:pr-6">
        <h1 className="border-b-4 border-black font-bold mb-4 text-xl uppercase">
          Reset Your Password
        </h1>
        {resetPasswordErrorMessage ? (
          <div className="text-red-700 text-sm mb-2">
            {resetPasswordErrorMessage}
          </div>
        ) : null}
        {resetPasswordValidMessage ? (
          <div className="text-green-700 text-sm mb-2">
            {resetPasswordValidMessage}
          </div>
        ) : null}
        <form onSubmit={handleResetPassword}>
          <div className="mb-4">
            <label
              className="block text-gray-700 text-xs uppercase font-bold"
              htmlFor="email"
            >
              Email Address
            </label>
            <input
              className="shadow appearance-none border rounded-sm w-full text-base px-2 py-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              type="email"
              placeholder="Email"
              required={true}
              value={resetPasswordData.user.email ?? ""}
              onChange={(e) => setResetPasswordField("email", e.target.value)}
            />
          </div>
          <div className="flex items-center justify-between">
            <SubmitButton
              className="disabled:cursor-not-allowed"
              active={!isSubmitting}
            >
              {isSubmitting
                ? "Sending Reset Password Email..."
                : "Send Reset Password Email"}
            </SubmitButton>
          </div>
        </form>
      </div>
      <div className="lg:col-span-3 col-span-12 lg:mt-0 mt-16">
        <h1 className="border-b-4 border-black font-bold mb-4 text-xl text-right">
          Got it
        </h1>
        <div className="float-right mt-4">
          <Button
            className="disabled:cursor-not-allowed"
            disabled={isSubmitting}
            onClick={() => setModalState("auth")}
          >
            Sign In
          </Button>
        </div>
      </div>
    </>
  )
}
